/**
 * The filter grammar: one schema, shared by the calendar query, the Initiative
 * list, the Saved_View payload and the export request.
 *
 * That sharing is not tidiness, it is R13.6: an export must contain exactly the
 * distinct Initiative identifiers the Calendar_View renders, with zero additional
 * and zero omitted records. The cheapest way to guarantee that is for the export
 * request to carry the *same* filter representation the screen used, so there is
 * no second grammar to keep in step.
 *
 * Reading conventions the schema encodes:
 *
 * - **Multi-value dimensions are arrays, empty when unfiltered.** R11.7 ANDs
 *   across dimensions and R11.8 ORs within one, so an empty array means "this
 *   dimension is not applied" and never "match nothing". Every dimension defaults
 *   to `[]`, which is what lets the calendar's entry state (R11.1) be the parse of
 *   an empty query string.
 * - **Boolean dimensions are tri-state.** `null` means unfiltered, `true` and
 *   `false` filter. A two-state Boolean would make "show me everything" and "show
 *   me the ones set to no" the same request.
 * - **Values are accepted from a query string and from a body alike**, comma
 *   separated or repeated, because filter state lives in the URL on the screen and
 *   in a body on the export request (design.md, "Filters and Saved Views").
 *
 * Stale stored values (R11.15, R11.16) are deliberately not a schema concern: a
 * deactivated value still parses and is still applied, and a value that no longer
 * exists is dropped at load time by joining against master data. A schema that
 * rejected either would turn a Saved_View into an unopenable one.
 */
import { z } from 'zod';
import { fieldForceRoleSchema, initiativeStatusSchema } from './enums.js';
import { milestoneReadinessFilterSchema, watchoutSeveritySchema } from './governance-enums.js';
import { booleanFilterSchema, idSchema, isoWeekSchema, listFilterSchema, monthSchema, planningYearSchema, searchFilterSchema, } from './primitives.js';
/**
 * The columns a Calendar_View or Initiative list may be ordered by.
 *
 * The sort order is part of the view (R11.10) and is reproduced in the export
 * metadata block (R15.3). Ties are broken by ascending Initiative identifier
 * (R15.4); that tie-break is not a wire value because it is not the user's choice.
 */
export const INITIATIVE_SORT_FIELDS = Object.freeze([
    'start_date',
    'end_date',
    'title',
    'initiative_ref',
    'planning_year',
    'status',
    'priority',
    'owner',
]);
/** See {@link INITIATIVE_SORT_FIELDS}. */
export const initiativeSortFieldSchema = z.enum(INITIATIVE_SORT_FIELDS);
/** Ascending or descending. */
export const sortDirectionSchema = z.enum(['asc', 'desc']);
/** The applied sort order of a view, defaulting to newest Initiative first (by identifier, descending). */
export const sortOrderSchema = z
    .object({
    field: initiativeSortFieldSchema.default('initiative_ref'),
    direction: sortDirectionSchema.default('desc'),
})
    .default({ field: 'initiative_ref', direction: 'desc' });
/**
 * Every filter dimension of R11.1 to R11.6.
 *
 * `planningYear` is the one required value: R11.1 sets it to the year of the
 * current system date on entry, and the calendar, the list and both exports are
 * always scoped to one planning year.
 */
export const initiativeFilterSchema = z.object({
    /** R11.1. Bounded as `sales_cycle.planning_year` is, so a nonsense year never reaches a range scan. */
    planningYear: planningYearSchema,
    /** R11.1. */
    salesCycleIds: listFilterSchema(idSchema),
    /** R11.1. */
    months: listFilterSchema(monthSchema),
    /** R11.1. */
    isoWeeks: listFilterSchema(isoWeekSchema),
    /** R11.2. */
    platformIds: listFilterSchema(idSchema),
    /** R11.2. */
    channelIds: listFilterSchema(idSchema),
    /** R11.2. */
    customerGroupIds: listFilterSchema(idSchema),
    /** R11.2. */
    audienceIds: listFilterSchema(idSchema),
    /** R11.3. */
    categoryIds: listFilterSchema(idSchema),
    /** R11.3. */
    initiativeTypeIds: listFilterSchema(idSchema),
    /** R11.4. */
    ownerUserIds: listFilterSchema(idSchema),
    /**
     * R11.4. Empty means the default scope, which excludes Cancelled: R9.7 and
     * R13.7 include Cancelled records only while the Cancelled status filter is
     * selected, in the rendering, the count and the export alike.
     */
    statuses: listFilterSchema(initiativeStatusSchema),
    /** R11.4. */
    priorityIds: listFilterSchema(idSchema),
    /**
     * R11.4. `true` selects Confidential_Initiatives, `false` the rest, `null` both.
     * It filters what the requesting user may already read; it never widens access
     * (R3.1).
     */
    confidential: booleanFilterSchema,
    /** R11.5. */
    fieldForceInvolvement: booleanFilterSchema,
    /** R11.5. Role level only - there is no employee dimension to filter on (R18.13). */
    fieldForceRoles: listFilterSchema(fieldForceRoleSchema),
    /** R11.5. */
    fieldForceMaterial: booleanFilterSchema,
    /** R11.5. Presence of a work order on any Field Force scope entry. */
    hasWorkOrder: booleanFilterSchema,
    /** R11.5. Presence of an information contribution on any Field Force scope entry. */
    hasInformationContribution: booleanFilterSchema,
    /** R11.6. */
    dteInvolvement: booleanFilterSchema,
    /** R11.6. */
    cscInvolvement: booleanFilterSchema,
    /**
     * R11.6. An open dependency is one whose completion state is not complete -
     * the same predicate the Detail_Panel renders (R12.3), which is one function in
     * `@complan/domain` rather than two definitions.
     */
    openDependencies: booleanFilterSchema,
    /**
     * `complan-governance` R6.11: Initiatives holding at least one
     * Unresolved_Watchout.
     *
     * Tri-state like every other Boolean dimension, and "unresolved" is
     * `@complan/domain`'s `isWatchoutOpen` - one definition shared with the
     * Detail_Panel list and the Governance_Dashboard count, in the same way
     * `openDependencies` above shares `isDependencyOpen`. `false` selects
     * Initiatives with no open watchout, which includes both those whose watchouts
     * are all resolved and those that have none.
     */
    hasUnresolvedWatchout: booleanFilterSchema,
    /**
     * `complan-governance` R6.11: the severity of an Unresolved_Watchout.
     *
     * Scoped to *unresolved* watchouts deliberately. A severity filter that also
     * matched resolved ones would report an Initiative as carrying a High concern
     * that was closed months ago, which is the opposite of what a governance
     * reader is asking. Combined with `hasUnresolvedWatchout` it narrows further
     * rather than conflicting: both are predicates over the same open set.
     */
    watchoutSeverities: listFilterSchema(watchoutSeveritySchema),
    /**
     * `complan-governance` R6.12: Initiatives by the presence of a Required_Milestone
     * in one of the three problem states.
     *
     * `planned` and `completed` are deliberately absent from the filterable set:
     * the dimension exists to find Initiatives needing attention, and "has a
     * milestone that is fine" selects almost the whole population. The states are
     * evaluated against the request's cut-off date and Readiness_Horizon, so the
     * same URL read on two different days can legitimately match different
     * Initiatives - which is true of every date-relative filter and is why the
     * dashboard displays its cut-off date (R3.12).
     */
    milestoneReadiness: listFilterSchema(milestoneReadinessFilterSchema),
    /**
     * The filter bar's free-text Search (UI spec 3.6, which names Search a binding
     * member of the filter set alongside the dimensions of R11.1-R11.6).
     *
     * One term matched case-insensitively against the text a user can see on a row:
     * the Initiative identifier, the Title, the Details, the Owner and Backup Owner
     * names, and the Platform and Sales_Cycle labels. It narrows the readable
     * relation like every other dimension and never widens it, so a term matching a
     * Confidential_Initiative the requesting user may not read still returns nothing
     * (R3.1, R3.2).
     */
    search: searchFilterSchema,
});
/**
 * A filter dimension as the export metadata block reports it (R15.3): the applied
 * values, or an explicit mark that the dimension was not filtered.
 */
export const appliedFilterDimensionSchema = z.object({
    dimension: z.string().min(1),
    applied: z.boolean(),
    values: z.array(z.string()).default([]),
});
//# sourceMappingURL=filters.js.map